import {AnyAction, IDocumentAction, isDocumentAction, updateDescription} from "@buildwithflux/core";

import type {UseDocumentMetaStore} from "modules/documentMeta/state/documentMeta";
import {updateName} from "modules/document/state/actionCreators/documentActions";
import {isBatchAction} from "modules/document/state/helpers/batchActions";

/**
 * Service that syncs document metadata changes to the Zustand DocumentMetaStore.
 *
 * This service receives actions via its onAction() method, called directly by the store's
 * service notification system after actions are processed. It syncs updateDescription/updateName
 * actions to the DocumentMetaStore's baseDocument.
 * This replaces the Redux documentMetaSyncMiddleware.
 */
export interface DocumentMetaSyncService {
    /**
     * Called when an action is processed by the document store.
     * Syncs relevant metadata changes (name, description) to the DocumentMetaStore.
     */
    onAction: (action: AnyAction) => void;

    dispose: () => void;
}

export interface DocumentMetaSyncServiceDependencies {
    useDocumentMetaStore: UseDocumentMetaStore;
}

/**
 * Creates a DocumentMetaSyncService instance.
 *
 * Receives actions via onAction() and syncs updateDescription/updateName
 * actions to the DocumentMetaStore.
 */
export function createDocumentMetaSyncService(
    dependencies: DocumentMetaSyncServiceDependencies,
): DocumentMetaSyncService {
    const {useDocumentMetaStore} = dependencies;
    let disposed = false;

    function processAction(action: IDocumentAction): void {
        if (action.type === updateDescription.type) {
            const {description} = action.payload;
            useDocumentMetaStore.getState().updateDocumentDescription(description);
        } else if (action.type === updateName.type) {
            const {newDocumentName, newDocumentSlug} = action.payload;
            useDocumentMetaStore.getState().updateDocumentName(newDocumentName, newDocumentSlug);
        }
    }

    return {
        onAction: (action: AnyAction) => {
            if (disposed) {
                return;
            }

            // Check if it's a batch action - if so, process each sub-action
            if (isBatchAction(action)) {
                for (const subAction of action.payload) {
                    processAction(subAction);
                }
            } else if (isDocumentAction(action)) {
                processAction(action);
            }
        },
        dispose: () => {
            disposed = true;
        },
    };
}
